import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Validation from "../../Validation/Validation";
import { userLogin } from "../../services/Auth";
import { useHistory } from "react-router";

const BRAND_RED = "#C62828";
const PANEL_DARK = "#1a2744";

function Login() {
  const history = useHistory();
  const [showError, setShowError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    setValues({ ...values, [event.target.name]: event.target.value });
    if (errors[event.target.name]) {
      setErrors({ ...errors, [event.target.name]: undefined });
    }
  };

  const handleFormSubmit = async (event) => {
    console.log("Submitting form with values:", values);
    event.preventDefault();
    const validationErrors = Validation(values);
    console.log("Validation errors:", validationErrors);
    setErrors(validationErrors);
    // if (Object.keys(validationErrors).length === 0) {
    if (!validationErrors.email && !validationErrors.password) {
      setLoading(true);
      console.log("Form values:", values);
      const result = await userLogin(values.email, values.password);
      setLoading(false);
      if (result === "done") {
        history.push("/template/home");
      } else {
        setShowError(result);
      }
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": { borderColor: BRAND_RED },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: BRAND_RED },
    "& .MuiInputBase-input": { color: "#111111" },
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* ── Left Brand Panel ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "44%",
          background: `linear-gradient(160deg, ${PANEL_DARK} 0%, #0d1422 100%)`,
          position: "relative",
          overflow: "hidden",
          p: 6,
        }}
      >
        {/* Decorative rings */}
        <Box sx={{
          position: "absolute", width: 380, height: 380, borderRadius: "50%",
          border: "1.5px solid rgba(198,40,40,0.18)", top: -120, right: -100,
        }} />
        <Box sx={{
          position: "absolute", width: 240, height: 240, borderRadius: "50%",
          border: "1.5px solid rgba(198,40,40,0.12)", top: -40, right: -20,
        }} />
        <Box sx={{
          position: "absolute", width: 260, height: 260, borderRadius: "50%",
          border: "1.5px solid rgba(198,40,40,0.14)", bottom: -70, left: -80,
        }} />
        <Box sx={{
          position: "absolute", width: 100, height: 100, borderRadius: "50%",
          bgcolor: "rgba(198,40,40,0.07)", bottom: 180, right: 60,
        }} />

        {/* Logo card */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 3,
            px: 4,
            py: 3,
            mb: 5,
            boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
            width: "72%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src="/images/logo.png"
            alt="INORTEL"
            style={{ width: "100%", maxWidth: 210, height: "auto" }}
          />
        </Box>

        <Typography variant="h5" sx={{ color: "white", fontWeight: 700, textAlign: "center", mb: 2 }}>
          Welcome Back
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.55)", textAlign: "center", lineHeight: 1.9, maxWidth: 280 }}
        >
          Sign in to Nortel business management system.
        </Typography>

        {/* Red accent bar at bottom */}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, bgcolor: BRAND_RED }} />
      </Box>

      {/* ── Right Form Panel ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#ffffff",
          p: { xs: 3, sm: 5, md: 7 },
        }}
      >
        {/* Mobile-only logo */}
        <Box sx={{ display: { xs: "flex", md: "none" }, mb: 4, justifyContent: "center" }}>
          <img src="/images/logo.png" alt="INORTEL" style={{ height: 52 }} />
        </Box>

        <Box sx={{ width: "100%", maxWidth: 420 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: PANEL_DARK, mb: 0.5 }}>
            Sign In
          </Typography>
          <Typography variant="body2" sx={{ color: "#607d8b", mb: 4 }}>
            Enter your credentials to access your account
          </Typography>

          {showError && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setShowError("")}
            >
              {showError}
            </Alert>
          )}

          <form onSubmit={handleFormSubmit} noValidate>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              variant="outlined"
              value={values.email}
              onChange={handleChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
              sx={{ mb: 2.5, ...fieldSx }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ color: errors.email ? "error.main" : "#90a4ae", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              value={values.password}
              onChange={handleChange}
              error={Boolean(errors.password)}
              helperText={errors.password}
              sx={{ mb: 1, ...fieldSx }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: errors.password ? "error.main" : "#90a4ae", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                      {showPassword
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                        : <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: "right", mb: 3.5 }}>
              <Link
                href="/forgotpassword"
                underline="hover"
                sx={{ color: BRAND_RED, fontSize: 13, fontWeight: 500 }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                bgcolor: BRAND_RED,
                "&:hover": { bgcolor: "#b71c1c" },
                borderRadius: 2,
                py: 1.5,
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: 0.5,
                boxShadow: "0 4px 14px rgba(198,40,40,0.35)",
                mb: 3,
                textTransform: "none",
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Sign In"}
            </Button>
          </form>

          <Typography variant="body2" align="center" sx={{ color: "#607d8b" }}>
            Don&apos;t have an account?{" "}
            <Link href="/register" underline="hover" sx={{ color: BRAND_RED, fontWeight: 600 }}>
              Sign up
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

Login.propTypes = {};

export default Login;
